import React from 'react';

import DeleteIcon from '@sbu/assets.svg/src/delete.svg';
import EditIcon from '@sbu/assets.svg/src/edit.svg';
import {
  DataGrid,
  DataGridHeaderActions,
  RowActionConfig,
} from '@sbu/components.datagrid';
import { DeleteDialog } from '@sbu/components.deletedialog';
import { useToasts } from '@sbu/contexts.toasts';
import { usePermission } from '@sbu/hooks.permissions';
import { Button } from '@sumologic/yama-ds-components';
import { PlusIcon } from '@sumologic/yama-ds-icons';

import {
  DeleteInsightResolutionOperationMutation as DeleteInsightResolutionOperation,
  DeleteInsightResolutionOperationMutationVariables as DeleteInsightResolutionOperationVariables,
  InsightResolutionQueryOperationQuery as InsightResolutionQueryOperation,
  InsightResolutionQueryOperationQueryVariables as InsightResolutionQueryOperationVariables,
} from 'core/graphql';
import { useMutation, useQuery } from 'shared/hooks';

import { ConfigPage } from '../ConfigPage';
import { WorkflowConfigPages } from '../ConfigPage/constants';
import { InsightResolutionsAside } from './InsightResolutionsAside';
import { useInsightResolutionsContext } from './InsightResolutionsContext';
import {
  DELETE_RESOLUTION_MUTATION,
  INSIGHT_RESOLUTIONS_QUERY,
} from './queries';
import { InsightResolutionRow, isParentResolution } from './types';
import { columns } from './utils';

const OBJECT_TYPE = 'Insight Resolution';

export const InsightResolutionsDataGrid = () => {
  const hasManagePermission = usePermission('can_manage_insight_statuses');
  const { addSuccess } = useToasts();

  const {
    selectedRow,
    rowToDelete,
    isDeleteDialogOpen,
    isAsideOpen,
    onClickRow,
    onEdit,
    onDelete,
    onAdd,
    onCancelDelete,
    onConfirmDelete,
  } = useInsightResolutionsContext();

  const { data, loading } = useQuery<
    InsightResolutionQueryOperation,
    InsightResolutionQueryOperationVariables
  >(INSIGHT_RESOLUTIONS_QUERY);

  const insightResolutions: InsightResolutionRow[] =
    data?.insightResolutions?.objects ?? [];

  const [deleteResolution, { loading: deleting }] = useMutation<
    DeleteInsightResolutionOperation,
    DeleteInsightResolutionOperationVariables
  >(DELETE_RESOLUTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: INSIGHT_RESOLUTIONS_QUERY }],
  });

  const rowActions: RowActionConfig<InsightResolutionRow>[] = [
    {
      color: 'primary',
      onClick: onEdit,
      getIcon: () => EditIcon,
      renderLabel: () => 'Edit',
      isHidden: (row) => isParentResolution(row) || !hasManagePermission,
    },
    {
      color: 'secondary',
      onClick: onDelete,
      getIcon: () => DeleteIcon,
      renderLabel: () => 'Delete',
      isHidden: (row) => isParentResolution(row) || !hasManagePermission,
    },
  ];

  const handleConfirmDelete = async () => {
    if (!rowToDelete?.id) {
      return;
    }

    const response = await deleteResolution({
      variables: { id: rowToDelete.id },
    });

    if (!response.errors) {
      onConfirmDelete();
      addSuccess('The insight resolution was deleted successfully');
    }
  };

  return (
    <ConfigPage currentPage={WorkflowConfigPages.Resolution}>
      <DataGrid
        data-sumo-test='insight-resolutions-data-grid'
        loading={loading}
        tableOptions={{
          data: insightResolutions,
          columns,
          initialState: {
            sorting: [
              {
                id: 'name',
                desc: false,
              },
            ],
          },
          getRowId: (row) => `${row.id}`,
          getSubRows: (row) =>
            isParentResolution(row) ? row.subResolutions : undefined,
          getRowCanExpand: (row) =>
            isParentResolution(row.original) &&
            !!row.original.subResolutions?.length,
        }}
        {...(hasManagePermission && {
          renderHeader: () => (
            <DataGridHeaderActions>
              <Button startIcon={<PlusIcon />} onClick={onAdd} size='small'>
                Add Resolution
              </Button>
            </DataGridHeaderActions>
          ),
        })}
        rowActions={rowActions}
        onClickRow={(_e, row) => onClickRow(row.original)}
        openRowId={selectedRow?.id ? `${selectedRow?.id}` : undefined}
        PaginationProps={{
          loading,
          total: data?.insightResolutions?.total ?? 0,
        }}
        EmptyStateProps={{
          objectType: OBJECT_TYPE,
          ...(hasManagePermission ? { onClickAdd: onAdd } : {}),
        }}
      />
      {isAsideOpen && (
        <InsightResolutionsAside
          rowActions={rowActions}
          parentResolutions={insightResolutions}
        />
      )}
      {rowToDelete && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          objectName={rowToDelete.name}
          objectType={OBJECT_TYPE}
          onCancel={onCancelDelete}
          onConfirm={handleConfirmDelete}
          deleting={deleting}
        />
      )}
    </ConfigPage>
  );
};
